import type { Category, Todo } from "../../domain/entities.ts";
import { animateRightPanelClose } from "../../ui/right-panel.ts";
import { boardCaptureTags } from "./board-capture.ts";
import { applyColumnDefaults } from "./board-configuration.ts";
import { localSlotInstant, type CalendarPlacement } from "./calendar-presentation.ts";
import { resolveWallTime, wallTimeInZone } from "./task-time.ts";
import { activeTags } from "./selectors.ts";
import { taskFormSignature } from "./task-form-controller.ts";

export interface TaskComposerPanelState { draft: Todo | null; selectedId: string | null; editing: boolean }

export interface TaskComposerWorkflowCapabilities {
  activeRoot(): string | null;
  editable(collectionId?: string): boolean;
  panel(): TaskComposerPanelState;
  setPanel(value: TaskComposerPanelState): void;
  categories(): Category[];
  categoryFilter(): string | null;
  emptyDraft(collectionId?: string): Todo;
  loadCreateRecovery(rootId: string): Promise<Todo | null>;
  clearCreateRecovery(): Promise<void>;
  clearEditRecovery(id: string): Promise<void>;
  refreshReminderDefaults(rootId: string): Promise<void>;
  setDraftRecovered(value: boolean): void;
  setRecoveredEditDraft(value: Todo | null): void;
  resetLastTrigger(): void;
  lastTrigger(): string | null;
  resetMoreDetails(): void;
  render(animate?: boolean): Promise<void>;
  startPresence(id: string): void;
  leavePresence(id: string): void;
  confirmDiscard(): Promise<boolean>;
  addDays(date: string, days: number): string;
  today(): string;
}

export function createTaskComposerWorkflow(capabilities: TaskComposerWorkflowCapabilities) {
  const focusTitle = () => {
    // Opening can finish after the user has moved to another field or search.
    // Never let the animation's delayed autofocus steal that newer focus.
    if (document.querySelector("dialog[open]")) return;
    const focused = document.activeElement;
    const panel = focused?.closest(".detail-panel");
    if (panel && focused !== panel) return;
    document.querySelector<HTMLInputElement>("#detail-form input[name=title]")?.focus();
  };

  const confirmDiscard = async (): Promise<boolean> => {
    const form = document.querySelector<HTMLFormElement>("#detail-form");
    if (!form || form.dataset.initialState === taskFormSignature(form)) return true;
    return capabilities.confirmDiscard();
  };

  const close = async (skipAnimation = false): Promise<boolean> => {
    if (!await confirmDiscard()) return false;
    const panel = capabilities.panel();
    const discardedDraft = !!panel.draft;
    const hadPanel = !!panel.selectedId || !!panel.draft;
    if (hadPanel && !skipAnimation) await animateRightPanelClose(document.querySelector<HTMLElement>(".detail-panel"), document.querySelector<HTMLElement>(".task-view-backdrop"));
    if (panel.editing && panel.selectedId) await capabilities.clearEditRecovery(panel.selectedId);
    if (panel.selectedId) capabilities.leavePresence(panel.selectedId);
    capabilities.setRecoveredEditDraft(null);
    capabilities.setPanel({ selectedId: null, draft: null, editing: false });
    if (discardedDraft) { await capabilities.clearCreateRecovery(); capabilities.setDraftRecovered(false); }
    capabilities.resetMoreDetails();
    if (hadPanel) {
      await capabilities.render();
      const trigger = capabilities.lastTrigger();
      if (trigger) document.querySelector<HTMLElement>(`[data-todo="${trigger}"]`)?.focus({ preventScroll: true });
    }
    return true;
  };

  return {
    confirmDiscard,
    close,
    async returnToView(): Promise<void> {
      const id = capabilities.panel().selectedId;
      if (!id || !await confirmDiscard()) return;
      await capabilities.clearEditRecovery(id);
      capabilities.setRecoveredEditDraft(null);
      capabilities.setPanel({ ...capabilities.panel(), editing: false });
      await capabilities.render(false);
      capabilities.startPresence(id);
      document.querySelector<HTMLElement>("[data-action=edit-task]")?.focus({ preventScroll: true });
    },
    async open(boardTag?: string, initialTitle = "", calendarSlot?: CalendarPlacement, timelineCollection?: string): Promise<void> {
      const rootId = capabilities.activeRoot();
      if (!rootId || !capabilities.editable(timelineCollection)) return;
      if(timelineCollection&&!capabilities.categories().some(item=>item.id===timelineCollection&&item.rootId===rootId&&!item.deleted&&!item.archivedAt))return;
      await capabilities.refreshReminderDefaults(rootId);
      if (!await close()) return;
      capabilities.resetLastTrigger();
      const recovered = await capabilities.loadCreateRecovery(rootId);
      capabilities.setDraftRecovered(!!recovered);
      const draft = recovered ? { ...recovered, rootId } : capabilities.emptyDraft(timelineCollection);
      if(timelineCollection)draft.categoryId=timelineCollection;
      if (boardTag !== undefined) {
        const category = capabilities.categories().find(item => item.id === capabilities.categoryFilter());
        const tags = boardCaptureTags(activeTags(draft), category?.boardColumns || [], boardTag || null);
        draft.categoryId = capabilities.categoryFilter(); draft.tags = Object.fromEntries(tags.map(tag => [tag, true])); draft.tagOrder = tags;
      }
      const draftCategory = capabilities.categories().find(item => item.id === draft.categoryId);
      const draftColumn = activeTags(draft)[0];
      if (!recovered && draftColumn && draftCategory?.boardColumns?.includes(draftColumn)) applyColumnDefaults(draft, draftCategory.boardColumnSettings?.[draftColumn], days => capabilities.addDays(capabilities.today(), days), !draftCategory.approvalRule?.reviewerIds.length || !!draftCategory.approvalRule.column && draftCategory.approvalRule.column !== draftColumn);
      if (initialTitle) draft.title = initialTitle;
      if (calendarSlot) {
        draft.startDate = calendarSlot.date;
        if(timelineCollection&&draft.dueDate&&draft.dueDate<calendarSlot.date)draft.dueDate=null;
        draft.startAt = null; draft.endAt = null; draft.wallStartAt = null; draft.wallEndAt = null;
        if (calendarSlot.minute !== undefined) {
          draft.startAt = localSlotInstant(calendarSlot.date, calendarSlot.minute);
          draft.estimatedMinutes = draft.estimatedMinutes || 60;
          draft.endAt = new Date(new Date(draft.startAt).getTime() + draft.estimatedMinutes * 60_000).toISOString();
          if (draft.scheduleMode === "floating" && draft.scheduleTimeZone) {
            draft.wallStartAt = wallTimeInZone(draft.startAt, draft.scheduleTimeZone);
            draft.wallEndAt = wallTimeInZone(draft.endAt, draft.scheduleTimeZone);
            draft.startAt = resolveWallTime(draft.wallStartAt, draft.scheduleTimeZone);
            draft.endAt = resolveWallTime(draft.wallEndAt, draft.scheduleTimeZone);
            draft.startDate = draft.wallStartAt.slice(0, 10);
          }
        }
      }
      capabilities.setPanel({ draft, selectedId: null, editing: true });
      await capabilities.render();
      const panel = document.querySelector<HTMLElement>(".detail-panel");
      if (!panel || window.matchMedia("(prefers-reduced-motion: reduce)").matches) focusTitle();
      else {
        const focusAfterOpening = (event: AnimationEvent) => { if (event.target !== panel || event.animationName !== "panel-in") return; panel.removeEventListener("animationend", focusAfterOpening); focusTitle(); };
        panel.addEventListener("animationend", focusAfterOpening);
      }
    },
  };
}
